import React, { useContext, useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useToasts } from 'react-toast-notifications';

import AlertBanner from '@components/common/AlertBanner/AlertBanner';
import BannerMango from '@components/common/BannerMango/BannerMango';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import useWindowSize from '@components/common/hooks/useWindowSize';
import SurveyModal from '@components/common/Survey/SurveyModal/SurveyModal/SurveyModal';
import { M_DEVICE } from '@components/lpc/Constants';
import PotContext from '@components/lpc/Context/Pot/PotContext';
import UserContext from '@components/lpc/Context/User/UserContext';
import NeedKycBanner from '@components/lpc/Dasboard/NeedKycBanner/NeedKycBanner';
import Layout from '@components/lpc/Layout/Layout';
import AcceptPotModal from '@components/lpc/Pot/AcceptPotModal/AcceptPotModal';
import ParticipateForm from '@components/lpc/Pot/AmountModule/Participate/ParticipateForm/ParticipateForm';
import Header from '@components/lpc/Pot/Header/Header';
import PotImageUpload from '@components/lpc/Pot/MainModule/Informations/PotImageUpload/PotImageUpload';
import ModuleContainer from '@components/lpc/Pot/ModuleContainer/ModuleContainer';
import PotHeaderBanner from '@components/lpc/Pot/PotHeaderBanner/PotHeaderBanner';
import { PotboardPage } from '@models/BannerModel/BannerPagesModel';
import { personRoles } from '@models/PotModel/personRoles';
import { partnerType } from '@propTypes/partnerTypes';
import { participationDataType, potObjectiveType, potType } from '@propTypes/potTypes';
import AlertsService from '@services/domain/Lpc/AlertsService';
import partnersService from '@services/domain/Lpc/PartnersService';
import PotService from '@services/domain/Lpc/PotService';
import { isInternalServerError, isNotFoundError, redirectToCustomUrl } from '@services/http/Http';
import { getRoute, ROUTE } from '@services/http/Route';
import ObjectService, { isEmpty } from '@services/utils/ObjectService';
import { getUrlWithoutParams, htmlToText } from '@services/utils/StringService';

import css from './potPage.module.scss';
import Custom404 from '../../../404';
import Custom500 from '../../../500';

declare global {
    // eslint-disable-next-line no-var
    var potCategory: string;
    // eslint-disable-next-line no-var
    var hasDefaultImage: boolean;
}

type PotPageProps = {
    potRef: string;
    internalError: boolean;
    initialPot: potType;
    potNotFound: boolean;
    favoritePartners: partnerType[];
};

const PotPage = ({ initialPot, internalError, potNotFound, favoritePartners }: PotPageProps) => {
    const { t } = useTranslateLpc([
        'lpc-partner-details',
        'lpc-pot',
        'lpc-footer',
        'lpc-pot-partner-giftcard',
        'common',
    ]);
    const { width } = useWindowSize();
    const router = useRouter();
    const [openSetting, setOpenSetting] = useState<boolean>(false);
    const [isTitleVisible, setIsTitleVisible] = useState(null);
    const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
    const [showSurveyModal, setShowSurveyModal] = useState(false);
    const potContext = useContext(PotContext);
    const { updatedPot, setUpdatedPot } = potContext;
    const [pot, setPot] = useState(initialPot);
    const { addToast } = useToasts();
    const isMobile = width <= M_DEVICE;

    const [globalAlerts, setGlobalAlerts] = useState({ enabled: false, frMessage: '' });
    const [showGlobalAlerts, setShowGlobalAlerts] = useState<boolean>(false);
    const [newPoolObjective, setNewPoolObjective] = useState<potObjectiveType>({});
    const userContext = useContext(UserContext);
    const { userAccount } = userContext;
    const [openParticipate, setOpenParticipate] = useState(false);
    const [participationRetry, setParticipationRetry] = useState<participationDataType>(null);

    useEffect(() => {
        if (router.query.participateRetry) {
            PotService.getPaymentResponse(router.query.participateRetry.toString()).then((res) => {
                setParticipationRetry(res);
                setOpenParticipate(true);
            });
        }
    }, [router.query.participateRetry]);

    useEffect(() => {
        router.query.participate && setOpenParticipate(true);
    }, [router.query.participate]);

    useEffect(() => {
        const alert = JSON.parse(localStorage.getItem('globalAlerts'));
        if (AlertsService.checkShownPossibility(alert, PotboardPage)) {
            setGlobalAlerts(alert);
            setShowGlobalAlerts(true);
        }
        setUpdatedPot(pot);
    }, []);

    useEffect(() => {
        if (router.asPath.startsWith('/pot-offer-validation/')) {
            if (!pot.isWaitingForOfferConfirmation) {
                router.push(getRoute(ROUTE.LPC.POT.POT, pot.uniq)).then(
                    addToast(t(`errors.OFFER_LINK_EXPIRED`), {
                        appearance: 'error',
                        autoDismiss: true,
                    }),
                );
            } else {
                setIsAcceptModalOpen(true);
            }
        }
        if (router.asPath.startsWith('/pot-offer-cancel/')) {
            PotService.cancelOfferPot(pot.uniq)
                .then(() => {
                    router.push(getRoute(ROUTE.LPC.POT.POT, pot.uniq)).then(
                        addToast(t('waiting-for-accept.cancel-cta.success'), {
                            appearance: 'success',
                            autoDismiss: true,
                        }),
                    );
                })
                .catch(() => {
                    router.push(getRoute(ROUTE.LPC.POT.POT, pot.uniq)).then(
                        addToast(t(`errors.DEFAULT_ERROR_MESSAGE`), {
                            appearance: 'error',
                            autoDismiss: true,
                        }),
                    );
                });
        }
        window.potCategory = initialPot.category;
        window.hasDefaultImage = !initialPot.image;
    }, [pot.uniq, router.asPath]);

    useEffect(() => {
        if (router.query.showSurvey === 'true') {
            setShowSurveyModal(true);
        }
    }, [router.query.showSurvey]);

    useEffect(() => {
        const { recentlyCreatedLps, recentlyCreatedLpc } = router.query;

        if (recentlyCreatedLps === 'true' || recentlyCreatedLpc === 'true') {
            const toastMessage = recentlyCreatedLps === 'true' ? t('lps-pot-created') : t('lpc-pot-created');
            addToast(toastMessage, { appearance: 'success', autoDismiss: true });
            window.history.replaceState({}, document.title, window.location.href.split('?')[0]);
        }
    }, [router.query?.recentlyCreatedLps, router.query?.recentlyCreatedLpc]);

    useEffect(() => {
        Object.keys(updatedPot).length && setPot(updatedPot);
    }, [updatedPot]);

    if (internalError) {
        return <Custom500 />;
    }

    if (potNotFound || ObjectService.isEmpty(pot)) {
        return <Custom404 potNotFound />;
    }

    return (
        <div className={css.pot_page_container}>
            <Layout
                potStatus={pot.status}
                isPotPage
                showLandBot={false}
                hideDropDown
                hideFooter={openParticipate}
                showTitleInNavBar={!openParticipate}
                showParticipate
                potRef={pot.uniq}
                poolContribution={pot.poolContribution}
                potTitle={pot.title}
                isTitleVisible={isTitleVisible}
                personRole={pot.personRole}
                isOffered={pot.isOffered}
                participateDisabled={pot.isFrozenAccount}
                setOpenParticipate={(value) => setOpenParticipate(value)}
                ogTitle={`Le pot commun.fr : ${pot.title}`}
                ogUrl={pot.absoluteUrl}
                ogDescription={htmlToText(pot.desc)}
                ogImage={pot.image || `/front-static/images/pot/categories/${pot.category}.png`}
            >
                {openParticipate && !isEmpty(pot.poolContribution) ? (
                    <ParticipateForm
                        participationRetry={participationRetry}
                        user={userAccount}
                        potRef={pot.uniq}
                        pot={pot}
                        poolContribution={pot.poolContribution}
                        commissionType={pot.commissionType}
                        onHide={() => setOpenParticipate(false)}
                    />
                ) : (
                    <>
                        <div className={css.potPageWrapper}>
                            {showGlobalAlerts && (
                                <AlertBanner
                                    message={globalAlerts.frMessage}
                                    customClass={css.homePageWrapper__alert_banner}
                                />
                            )}
                            <AcceptPotModal
                                potRef={pot.uniq}
                                uniqUP={router.query.uniqPO.toString()}
                                isVisible={isAcceptModalOpen}
                                initialTitle={t('approval.title', pot.title)}
                                initialConfirmLabel={t('approval.accept')}
                                initialCancelLabel={t('approval.refuse')}
                                initialDescription={t('approval.desc')}
                                cancelLink={pot.uniq}
                            />
                            <>
                                <NeedKycBanner
                                    customClass={css.potPageWrapper__KycBanner}
                                    personRole={pot.personRole}
                                />
                                {width < M_DEVICE && (
                                    <PotImageUpload
                                        personRole={pot.personRole}
                                        image={
                                            pot.image ||
                                            (pot.category === 'SOLIDARITY_POOL'
                                                ? null
                                                : `/front-static/images/pot/categories/${pot.category}.png`)
                                        }
                                        potRef={pot.uniq}
                                        potStatus={pot.status}
                                        hidePoolImage={pot.hidePoolImage}
                                        category={pot.category}
                                        isOffered={pot.isOffered}
                                        mobileView={width < M_DEVICE}
                                    />
                                )}
                                {!!pot.mangopayCguStatus && pot.mangopayCguStatus !== 'ACCEPTED' && <BannerMango />}
                                <Header
                                    hasImage={pot.category !== 'SOLIDARITY_POOL' || (pot.image && pot.image !== '')}
                                    setIsTitleVisible={(value) => setIsTitleVisible(value)}
                                    personRole={pot.personRole}
                                    potRef={pot.uniq}
                                    title={pot.title}
                                    eventDate={pot.eventDate}
                                    privacy={pot.privacy}
                                    potStatus={pot.status}
                                    isOffered={pot.isOffered}
                                    isFrozenAccount={pot.isFrozenAccount}
                                    mobileSize={width < M_DEVICE}
                                />

                                <ModuleContainer
                                    mobileView={width < M_DEVICE}
                                    pot={pot}
                                    onToggleOpenSettings={(value) => setOpenSetting(value)}
                                    favoritePartners={favoritePartners}
                                    newPoolObjective={(objective) => setNewPoolObjective(objective)}
                                    objective={newPoolObjective}
                                    setOpenParticipate={(value) => setOpenParticipate(value)}
                                />

                                {pot.personRole === personRoles.ORGANISER && isMobile && (
                                    <PotHeaderBanner
                                        desc={t('activity.banner.desc')}
                                        pot={pot}
                                        newPoolObjective={(objective) => setNewPoolObjective(objective)}
                                        openSetting={openSetting}
                                    />
                                )}
                            </>
                        </div>
                        <SurveyModal isVisible={showSurveyModal} onHide={() => setShowSurveyModal(false)} />
                    </>
                )}
            </Layout>
        </div>
    );
};

export default PotPage;

export const getServerSideProps = async (ctx) => {
    let pot;
    let reference = ctx.query.uniqPO;

    if (!reference || reference === 'undefined') {
        return {
            props: {
                initialPot: {},
                internalError: false,
                potNotFound: true,
                favoritePartners: [],
            },
        };
    }

    let internalError = false;
    let notFoundError = false;
    if (reference.endsWith('?showSettings=true')) {
        reference = getUrlWithoutParams(reference);
    }
    (ctx.req.originalUrl.startsWith('/pot-offer-validation/') ||
        ctx.req.originalUrl.startsWith('/pot-offer-cancel/')) &&
    reference
        ? (pot = await PotService.getOfferedPot(reference, ctx).catch((err) => {
              notFoundError = isNotFoundError(err);
              internalError = isInternalServerError(err);
          }))
        : (pot = await PotService.getPot(reference, ctx).catch((err) => {
              notFoundError = isNotFoundError(err);
              internalError = isInternalServerError(err);
          }));

    const mangoStatus = pot.mangopayCguStatus;

    if (
        !ctx.req.originalUrl.startsWith('/pot-offer-validation/') &&
        ctx.query.participate &&
        pot &&
        pot.customUniq &&
        reference !== pot.uniq + '-' + pot.customUniq
    ) {
        const customUrl = ROUTE.LPC.POT.POT + '/' + pot.uniq + '-' + pot.customUniq + '?participate=true';
        redirectToCustomUrl(customUrl, ctx);
    }

    if (
        !ctx.req.originalUrl.startsWith('/pot-offer-validation/') &&
        pot &&
        pot.customUniq &&
        reference !== pot.uniq + '-' + pot.customUniq
    ) {
        const customUrl = ROUTE.LPC.POT.POT + '/' + pot.uniq + '-' + pot.customUniq;
        redirectToCustomUrl(customUrl, ctx);
    }
    const favoritePartners = await partnersService.getFavoritePartners(ctx);

    return {
        props: {
            initialPot: pot || {},
            internalError: internalError,
            potNotFound: notFoundError,
            favoritePartners: favoritePartners,
            mangoStatus: mangoStatus,
        },
    };
};
